import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { HTML_LINK_TARGET } from '../../../../../constants/html'
import {
  useInitData,
  useLinkLikeClick,
  useNotification,
} from '../../../../../hooks'
import { stopPropagation } from '../../../../../utils/events'
import {
  getEmailProfilePathname,
  getRequestProfilePathname,
} from '../../../../../utils/link'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  EmailAddNoteActionModal,
  getEmailAddNoteInitialValues,
} from '../../../../Email'
import { REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY } from '../../../../RequestProfile'
import {
  deleteEntityEmailArchiveCopy,
  updateEntityEmailArchiveNote,
} from '../../EntityTabEmails.api'
import {
  ENTITY_TAB_EMAILS_ACTION,
  ENTITY_TAB_EMAILS_DELETE_COPY_CONFIRMATION,
} from '../../EntityTabEmails.constants'
import { getEntityTabEmailsMessageDeepLink } from '../../EntityTabEmails.utils'

export function useEntityTabEmailsActions({
  onCloseModal,
  onReload,
  onSetModal,
  onSuccessDeleteCopy = onReload,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { handleClick: handleLinkClick } = useLinkLikeClick()

  const { availableSections } = useInitData()

  const { pathname, search } = useLocation()

  function openInNewTab(item) {
    const uuid = item?.uuid

    if (uuid) {
      handleLinkClick({
        link: getEntityTabEmailsMessageDeepLink({ pathname, search, uuid }),
        target: HTML_LINK_TARGET.blank,
      })
    }
  }

  function openEmailProfile(uuid) {
    handleLinkClick({
      link: getEmailProfilePathname({
        availableSections,
        id: uuid,
        singleEmailAccount: true,
      }),
      target: HTML_LINK_TARGET.blank,
    })
  }

  function openOriginalEmail(item) {
    const original = item?.original_for_archive

    if (original?.request_id && original?.request_thread_uuid) {
      handleLinkClick({
        link: getRequestProfilePathname({
          additionalParams: `?${REQUEST_PROFILE_TAB_EMAILS_THREAD_QUERY_PARAM_KEY}=${original.request_thread_uuid}`,
          availableSections,
          id: original.request_id,
        }),
        target: HTML_LINK_TARGET.blank,
      })
    } else if (original?.uuid) {
      openEmailProfile(original.uuid)
    }
  }

  async function handleSubmitAddNote(id, values) {
    // API rejects an empty string for this field, so send null instead.
    return updateEntityEmailArchiveNote({
      notes: values.notes || null,
      uuid: id,
    })
      .then(() => {
        onCloseModal()
        handleAddSuccessNotification('Notification.EmailTitleAddNoteKey')

        return onReload?.()
      })
      .catch(() => {
        onCloseModal()
        handleAddErrorNotification('Notification.EmailAddNoteErrorKey')
      })
  }

  function openAddNoteModal(item) {
    onSetModal(
      <EmailAddNoteActionModal
        id={item?.uuid}
        initialValues={getEmailAddNoteInitialValues(item?.notes)}
        onClose={onCloseModal}
        onSubmit={handleSubmitAddNote}
      />
    )
  }

  function openDeleteCopyModal(item) {
    const uuid = item?.uuid

    function handleSubmitDelete(event) {
      stopPropagation(event)
      onCloseModal()

      return deleteEntityEmailArchiveCopy({ uuid })
        .then(() => {
          handleAddSuccessNotification(
            'Notification.EmailProfileMessageActionTitleRemoveCopyToEntityKey'
          )

          return onSuccessDeleteCopy?.()
        })
        .catch(() => {
          handleAddErrorNotification(
            'Notification.EmailProfileMessageActionErrorTitleRemoveCopyToEntityKey'
          )
        })
    }

    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t(ENTITY_TAB_EMAILS_DELETE_COPY_CONFIRMATION.SubmitKey),
        }}
        description={t(
          ENTITY_TAB_EMAILS_DELETE_COPY_CONFIRMATION.DescriptionKey
        )}
        onCancel={onCloseModal}
        onSubmit={handleSubmitDelete}
      />
    )
  }

  function setItemAction(action, item) {
    switch (action) {
      case ENTITY_TAB_EMAILS_ACTION.OpenInNewTab:
        openInNewTab(item)
        break
      case ENTITY_TAB_EMAILS_ACTION.ViewOriginalEmail:
        openOriginalEmail(item)
        break
      case ENTITY_TAB_EMAILS_ACTION.AddNote:
        openAddNoteModal(item)
        break
      case ENTITY_TAB_EMAILS_ACTION.DeleteCopy:
        openDeleteCopyModal(item)
        break
      default:
        break
    }
  }

  return {
    setItemAction,
  }
}
